import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Home, DollarSign, BookOpen, MapPin } from 'react-feather';

const PropertyDetailsModal = ({ property, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  // Reset internal state whenever a new property is loaded. Without this, the
  // modal stays on whatever photo / tab the agent left it on, since the
  // component instance persists across opens (e.g. clicking a different
  // property in BuyerDetailsModal's liked-properties panel doesn't unmount).
  useEffect(() => {
    setCurrentImageIndex(0);
    setActiveTab('overview');
  }, [property?._id || property?.id]);

  if (!isOpen || !property) return null;

  const lm = true;

  const t = {
    surface:      lm ? '#ffffff'                 : '#1a1a1a',
    surfaceAlt:   lm ? '#f2f1ee'                 : '#212121',
    surfaceDark:  lm ? '#eeede9'                 : '#151515',
    border:       lm ? 'rgba(26,26,26,0.1)'      : 'rgba(255,255,255,0.08)',
    text:         lm ? '#111111'                 : '#f0f0f0',
    textSub:      lm ? 'rgba(26,26,26,0.5)'      : 'rgba(255,255,255,0.45)',
    textMid:      lm ? 'rgba(26,26,26,0.72)'     : 'rgba(255,255,255,0.7)',
    accent:       '#6366f1',
    accentLight:  lm ? 'rgba(99,102,241,0.09)'   : 'rgba(99,102,241,0.14)',
    accentBorder: lm ? 'rgba(99,102,241,0.28)'   : 'rgba(99,102,241,0.38)',
    gold:         '#fdd700',
    goldLight:    lm ? 'rgba(253,215,0,0.13)'    : 'rgba(253,215,0,0.1)',
    green:        '#22c55e',
    greenLight:   lm ? 'rgba(34,197,94,0.1)'     : 'rgba(34,197,94,0.1)',
    shadow:       lm ? '0 32px 80px rgba(0,0,0,0.14)' : '0 32px 80px rgba(0,0,0,0.6)',
    overlay:      lm ? 'rgba(0,0,0,0.2)'         : 'rgba(0,0,0,0.72)',
  };

  const fmt = (n) => (n != null && n !== 0) ? '$' + Number(n).toLocaleString() : null;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const scoreColor = (s) => s >= 70 ? t.green : s >= 40 ? t.gold : '#ef4444';
  const scoreLabel = (s, type) => {
    if (type === 'walk') return s >= 90 ? "Walker's Paradise" : s >= 70 ? 'Very Walkable' : s >= 50 ? 'Somewhat Walkable' : 'Car-Dependent';
    if (type === 'transit') return s >= 90 ? "Rider's Paradise" : s >= 70 ? 'Excellent Transit' : s >= 50 ? 'Good Transit' : s >= 25 ? 'Some Transit' : 'Minimal Transit';
    if (type === 'bike') return s >= 90 ? "Biker's Paradise" : s >= 70 ? 'Very Bikeable' : s >= 50 ? 'Bikeable' : 'Not Very Bikeable';
  };

  const allImages = property.images?.length > 0 ? property.images : property.mainImage ? [property.mainImage] : [];

  // ─── Primitives ──────────────────────────────────────────────────────────────
  const SectionLabel = ({ children, mt }) => (
    <div style={{ fontSize: '0.7rem', fontWeight: '700', color: t.textSub, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: '10px', marginTop: mt || 0 }}>{children}</div>
  );

  const InfoRow = ({ label, value }) => (value != null && value !== '' && value !== false) ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: '1px solid ' + t.border }}>
      <span style={{ fontSize: '0.8rem', color: t.textSub, flexShrink: 0, paddingRight: '16px' }}>{label}</span>
      <span style={{ fontSize: '0.82rem', color: t.text, fontWeight: '500', textAlign: 'right' }}>{value}</span>
    </div>
  ) : null;

  const ScoreBar = ({ label, score, type, emoji }) => {
    if (!score) return null;
    return (
      <div style={{ marginBottom: '13px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '0.8rem', color: t.textMid }}>{emoji} {label}</span>
          <span style={{ fontSize: '0.77rem', fontWeight: '600', color: scoreColor(score) }}>{score} · {scoreLabel(score, type)}</span>
        </div>
        <div style={{ height: '4px', borderRadius: '2px', backgroundColor: t.border, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: '2px', width: score + '%', backgroundColor: scoreColor(score) }} />
        </div>
      </div>
    );
  };

  const SchoolCard = ({ school, level }) => {
    if (!school?.name) return null;
    return (
      <div style={{ padding: '10px 0', borderBottom: '1px solid ' + t.border }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: t.textSub, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{level}</div>
            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: t.text }}>{school.name}</div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '3px' }}>
              {school.grades && <span style={{ fontSize: '0.72rem', color: t.textSub }}>Grades {school.grades}</span>}
              {school.type && <span style={{ fontSize: '0.72rem', color: t.textSub }}>{school.type}</span>}
            </div>
          </div>
          {school.rating != null && (
            <div style={{ backgroundColor: school.rating >= 7 ? t.greenLight : t.goldLight, border: '1px solid ' + (school.rating >= 7 ? t.green : t.gold), color: school.rating >= 7 ? t.green : t.gold, borderRadius: '6px', padding: '3px 9px', fontSize: '0.82rem', fontWeight: '700', flexShrink: 0 }}>{school.rating}/10</div>
          )}
        </div>
      </div>
    );
  };

  // ─── Tabs ────────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview',   label: 'Overview',   icon: <Home size={12} /> },
    { id: 'details',    label: 'Details',    icon: <BookOpen size={12} /> },
    { id: 'location',   label: 'Location',   icon: <MapPin size={12} /> },
    { id: 'financials', label: 'Financials', icon: <DollarSign size={12} /> },
  ];

  // ─── Overview ────────────────────────────────────────────────────────────────
  const OverviewTab = () => {
    const nd = property.neighborhoodData;
    const schools = property.enrichedData?.schools || nd?.schools;
    const mlsSchools = property.mlsData?.mlsSchools;
    const hasSchools = schools?.elementary?.name || schools?.middle?.name || schools?.high?.name || mlsSchools?.elementarySchool || mlsSchools?.middleOrJuniorSchool || mlsSchools?.highSchool;

    return (
      <div>
        {/* Address + price header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: t.text, lineHeight: 1.3 }}>{property.address}</div>
            {property.csz && <div style={{ fontSize: '0.8rem', color: t.textSub, marginTop: '3px' }}>{property.csz}</div>}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '16px' }}>
            <div style={{ fontSize: '1.3rem', fontWeight: '800', color: t.text, lineHeight: 1 }}>{property.price}</div>
            {property.mlsData?.pricePerSqFt && <div style={{ fontSize: '0.72rem', color: t.textSub, marginTop: '3px' }}>${property.mlsData.pricePerSqFt}/sqft</div>}
          </div>
        </div>

        {/* Stat badges */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '22px', paddingBottom: '20px', borderBottom: '1px solid ' + t.border }}>
          {property.bedrooms != null && (
            <div style={{ backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: t.text, lineHeight: 1 }}>{property.bedrooms}</div>
              <div style={{ fontSize: '0.68rem', color: t.textSub, marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Beds</div>
            </div>
          )}
          {property.bathrooms != null && (
            <div style={{ backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: t.text, lineHeight: 1 }}>{property.bathrooms}</div>
              <div style={{ fontSize: '0.68rem', color: t.textSub, marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Baths</div>
            </div>
          )}
          {property.sqft > 0 && (
            <div style={{ backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: t.text, lineHeight: 1 }}>{property.sqft.toLocaleString()}</div>
              <div style={{ fontSize: '0.68rem', color: t.textSub, marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sq Ft</div>
            </div>
          )}
        </div>

        {/* Description */}
        {(property.description || property.mlsData?.publicRemarks) && (
          <div style={{ marginBottom: '22px' }}>
            <SectionLabel>Description</SectionLabel>
            <p style={{ fontSize: '0.875rem', color: t.textMid, lineHeight: '1.75', margin: 0, textAlign: 'left' }}>
              {property.description || property.mlsData?.publicRemarks}
            </p>
          </div>
        )}

        {/* Features */}
        {property.features?.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <SectionLabel>Features</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {property.features.map((f, i) => (
                <div key={i} style={{ fontSize: '0.85rem', color: t.textMid, padding: '6px 0', borderBottom: '1px solid ' + t.border }}>
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Neighborhood */}
        {(hasSchools || nd?.description) && (
          <div style={{ borderTop: '1px solid ' + t.border, paddingTop: '20px', marginTop: '4px' }}>
            <SectionLabel>Neighborhood</SectionLabel>

            {nd?.description && (
              <p style={{ fontSize: '0.875rem', color: t.textMid, lineHeight: '1.7', margin: '0 0 16px 0' }}>{nd.description}</p>
            )}

    

            {hasSchools && (
              <div>
                <SectionLabel mt="4px">Schools{(schools?.district || mlsSchools?.schoolDistrict) ? ' — ' + (schools?.district || mlsSchools?.schoolDistrict) : ''}</SectionLabel>
                {schools?.elementary?.name ? <SchoolCard school={schools.elementary} level="Elementary" />
                  : mlsSchools?.elementarySchool && <div style={{ fontSize: '0.85rem', color: t.textMid, padding: '6px 0', borderBottom: '1px solid ' + t.border }}>Elementary: {mlsSchools.elementarySchool}</div>}
                {schools?.middle?.name ? <SchoolCard school={schools.middle} level="Middle" />
                  : mlsSchools?.middleOrJuniorSchool && <div style={{ fontSize: '0.85rem', color: t.textMid, padding: '6px 0', borderBottom: '1px solid ' + t.border }}>Middle: {mlsSchools.middleOrJuniorSchool}</div>}
                {schools?.high?.name ? <SchoolCard school={schools.high} level="High School" />
                  : mlsSchools?.highSchool && <div style={{ fontSize: '0.85rem', color: t.textMid, padding: '6px 0', borderBottom: '1px solid ' + t.border }}>High School: {mlsSchools.highSchool}</div>}
              </div>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ marginTop: '28px', padding: '14px 16px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
          <div style={{ fontSize: '0.72rem', color: t.textSub, lineHeight: '1.65' }}>
            <strong style={{ color: t.textMid, fontWeight: '600' }}>Data Notice</strong> — Property details are sourced from MLS data and public records and may not be complete or current. Square footage, room counts, and listing details should be independently verified before advising clients.
          </div>
        </div>
      </div>
    );
  };

  // ─── Details ─────────────────────────────────────────────────────────────────
  const DetailsTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
      <div>
        <SectionLabel>Property</SectionLabel>
        <InfoRow label="Type" value={property.propertyType} />
        <InfoRow label="Year Built" value={property.yearBuilt} />
        <InfoRow label="Year Renovated" value={property.yearRenovated} />
        <InfoRow label="Lot Size" value={property.lotSize > 0 ? property.lotSize.toLocaleString() + ' sqft' : null} />
        <InfoRow label="Lot (acres)" value={property.mlsData?.mlsHomeDetails?.lotSizeAcres} />
        <InfoRow label="Construction" value={property.construction || property.enrichedData?.construction} />
        <InfoRow label="Basement" value={property.basementType || property.enrichedData?.basementType} />
        <InfoRow label="Basement Sq Ft" value={(property.basementSquareFeet || property.enrichedData?.basementSquareFeet) > 0 ? (property.basementSquareFeet || property.enrichedData?.basementSquareFeet)?.toLocaleString() + ' sqft' : null} />
        <InfoRow label="Attic" value={(property.attic || property.enrichedData?.attic) ? 'Yes' : null} />
        <InfoRow label="Fireplaces" value={(property.fireplaceCount || property.enrichedData?.fireplaceCount) > 0 ? (property.fireplaceCount || property.enrichedData?.fireplaceCount) : null} />
        <InfoRow label="Stories" value={property.mlsData?.mlsPropertyDetails?.stories} />
        <InfoRow label="Subdivision" value={property.mlsData?.mlsPropertyDetails?.subdivisionName || property.enrichedData?.subdivision} />
        <SectionLabel mt="22px">Systems</SectionLabel>
        <InfoRow label="Heating" value={property.heatingSystem || property.mlsData?.mlsHomeDetails?.heating} />
        <InfoRow label="Heating Fuel" value={property.heatingFuelType || property.enrichedData?.heatingFuelType} />
        <InfoRow label="Cooling" value={property.coolingSystem || property.mlsData?.mlsHomeDetails?.cooling} />
        <InfoRow label="Roof" value={property.mlsData?.mlsHomeDetails?.roof} />
        <InfoRow label="Sewer" value={property.mlsData?.mlsHomeDetails?.sewer} />
        <InfoRow label="Water" value={property.mlsData?.mlsHomeDetails?.watersource} />
        <InfoRow label="Flooring" value={property.mlsData?.mlsHomeDetails?.flooring?.join(', ')} />
        <InfoRow label="Exterior" value={property.mlsData?.mlsHomeDetails?.exteriorFeatures} />
      </div>
      <div>
        <SectionLabel>Parking & Laundry</SectionLabel>
        <InfoRow label="Parking" value={property.parking?.type && property.parking.type !== 'None' ? property.parking.type : null} />
        <InfoRow label="Spaces" value={property.parking?.spaces > 0 ? property.parking.spaces : null} />
        <InfoRow label="Garage Sq Ft" value={property.garage?.squareFeet > 0 ? property.garage.squareFeet + ' sqft' : null} />
        <InfoRow label="Parking Fee" value={property.parking?.additionalFee > 0 ? fmt(property.parking.additionalFee) + '/mo' : null} />
        <InfoRow label="Laundry" value={property.laundry?.type && property.laundry.type !== 'None' ? property.laundry.type : null} />
        <SectionLabel mt="22px">Pet Policy</SectionLabel>
        {(property.petPolicy?.allowsDogs || property.petPolicy?.allowsCats) ? (
          <>
            <InfoRow label="Dogs" value={property.petPolicy.allowsDogs ? 'Allowed' : 'Not allowed'} />
            <InfoRow label="Cats" value={property.petPolicy.allowsCats ? 'Allowed' : 'Not allowed'} />
            <InfoRow label="Pet Deposit" value={property.petPolicy.petDeposit > 0 ? fmt(property.petPolicy.petDeposit) : null} />
            <InfoRow label="Pet Rent" value={property.petPolicy.petRent > 0 ? fmt(property.petPolicy.petRent) + '/mo' : null} />
            <InfoRow label="Restrictions" value={property.petPolicy.petRestrictions || null} />
          </>
        ) : <div style={{ fontSize: '0.82rem', color: t.textSub, padding: '6px 0' }}>No pet policy on file</div>}
        {(property.listedBy?.agentName || property.mlsData?.mlsAgent?.fullName) && (
          <>
            <SectionLabel mt="22px">Listing Agent</SectionLabel>
            <InfoRow label="Agent" value={property.listedBy?.agentName || property.mlsData?.mlsAgent?.fullName} />
            <InfoRow label="Agency" value={property.listedBy?.agency || property.mlsData?.mlsOffice?.name} />
            <InfoRow label="Phone" value={property.listedBy?.contactPhone || property.mlsData?.mlsAgent?.phone} />
          </>
        )}
        {property.appliances?.length > 0 && (
          <>
            <SectionLabel mt="22px">Appliances</SectionLabel>
            {property.appliances.map((a, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: t.textMid, padding: '6px 0', borderBottom: '1px solid ' + t.border }}>{a}</div>
            ))}
          </>
        )}
      </div>

      {/* Disclaimer */}
      <div style={{ gridColumn: '1 / -1', marginTop: '8px', padding: '14px 16px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
        <div style={{ fontSize: '0.72rem', color: t.textSub, lineHeight: '1.65' }}>
          <strong style={{ color: t.textMid, fontWeight: '600' }}>Data Notice</strong> — Property specifications, system details, and feature information are sourced from MLS data and public records. All details should be independently verified with the appropriate parties prior to advising clients.
        </div>
      </div>
    </div>
  );

  // ─── Financials ──────────────────────────────────────────────────────────────
  const FinancialsTab = () => {
    const tax = property.taxInfo || property.enrichedData?.taxInfo;
    const saleHistory = property.saleHistory || property.enrichedData?.saleHistory || [];
    return (
      <div>
        <SectionLabel>Pricing</SectionLabel>
        <InfoRow label="List Price" value={property.price} />
        <InfoRow label="Price / Sq Ft" value={property.mlsData?.pricePerSqFt ? '$' + property.mlsData.pricePerSqFt : null} />
        <InfoRow label="Est. Mortgage" value={(property.estimatedMortgage || property.enrichedData?.estimatedMortgagePayment) > 0 ? fmt(property.estimatedMortgage || property.enrichedData?.estimatedMortgagePayment) + '/mo' : null} />
        <InfoRow label="HOA Fees" value={property.hoaFees > 0 ? fmt(property.hoaFees) + '/mo' : (property.hasHOA ? 'Yes (amount N/A)' : null)} />
        <InfoRow label="Property Tax" value={property.propertyTax > 0 ? fmt(property.propertyTax) + '/yr' : null} />

        {tax && (
          <>
            <SectionLabel mt="22px">Tax Information</SectionLabel>
            <InfoRow label="Annual Tax" value={fmt(tax.taxAmount)} />
            <InfoRow label="Assessment Year" value={tax.assessmentYear} />
            <InfoRow label="Assessed Value" value={fmt(tax.assessedValue)} />
            <InfoRow label="Market Value" value={fmt(tax.marketValue)} />
            <InfoRow label="Land Value" value={fmt(tax.assessedLandValue)} />
            <InfoRow label="Improvement Value" value={fmt(tax.assessedImprovementValue)} />
          </>
        )}

        {saleHistory.length > 0 && (
          <>
            <SectionLabel mt="22px">Sale History</SectionLabel>
            {saleHistory.slice(0, 6).map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid ' + t.border }}>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: t.text }}>{fmt(s.saleAmount)}</div>
                  <div style={{ fontSize: '0.73rem', color: t.textSub, marginTop: '2px' }}>{s.transactionType || s.documentType}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.8rem', color: t.textMid }}>{fmtDate(s.saleDate)}</div>
                  {s.purchaseMethod && <div style={{ fontSize: '0.7rem', color: t.textSub, marginTop: '1px' }}>{s.purchaseMethod}</div>}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Disclosure */}
        <div style={{ marginTop: '28px', padding: '14px 16px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
          <div style={{ fontSize: '0.72rem', color: t.textSub, lineHeight: '1.65' }}>
            <strong style={{ color: t.textMid, fontWeight: '600' }}>Data Disclaimer</strong> — Financial figures shown are sourced from MLS data and public records and may not reflect current or accurate values. All pricing, tax, HOA, and fee information should be independently verified. Agents are responsible for confirming all details with the appropriate parties prior to advising clients.
          </div>
        </div>
      </div>
    );
  };

  // ─── Location ────────────────────────────────────────────────────────────────
  const LocationTab = () => {
    // Resolve coordinates across possible shapes:
    //   1. GeoJSON Point on property.location → coordinates: [lng, lat]
    //   2. MLS-flat fields → property.mlsData.mlsHomeDetails.latitude / longitude
    //   3. Top-level latitude / longitude
    let lat = null, lng = null;
    const geo = property.location?.coordinates;
    if (Array.isArray(geo) && geo.length === 2 && typeof geo[0] === 'number' && typeof geo[1] === 'number') {
      // GeoJSON is [lng, lat]
      lng = geo[0];
      lat = geo[1];
    } else if (property.mlsData?.mlsHomeDetails?.latitude && property.mlsData?.mlsHomeDetails?.longitude) {
      lat = property.mlsData.mlsHomeDetails.latitude;
      lng = property.mlsData.mlsHomeDetails.longitude;
    } else if (typeof property.latitude === 'number' && typeof property.longitude === 'number') {
      lat = property.latitude;
      lng = property.longitude;
    }

    const hasCoords = lat != null && lng != null;
    const fullAddress = [property.address, property.csz].filter(Boolean).join(', ');

    if (!hasCoords) {
      return (
        <div>
          <SectionLabel>Map</SectionLabel>
          <div style={{ padding: '40px 20px', textAlign: 'center', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
            <MapPin size={28} style={{ color: t.textSub, marginBottom: '10px' }} />
            <div style={{ fontSize: '0.85rem', color: t.textMid, marginBottom: '4px' }}>Map unavailable</div>
            <div style={{ fontSize: '0.75rem', color: t.textSub }}>No GPS coordinates on file for this property.</div>
          </div>
          {fullAddress && (
            <>
              <SectionLabel mt="22px">Address</SectionLabel>
              <div style={{ fontSize: '0.85rem', color: t.text, padding: '6px 0' }}>{fullAddress}</div>
            </>
          )}
          <div style={{ marginTop: '28px', padding: '14px 16px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
            <div style={{ fontSize: '0.72rem', color: t.textSub, lineHeight: '1.65' }}>
              <strong style={{ color: t.textMid, fontWeight: '600' }}>Data Notice</strong> — Property location is sourced from MLS and public records. Confirm exact location independently before showings.
            </div>
          </div>
        </div>
      );
    }

    const embedSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
    const externalUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

    return (
      <div>
        <SectionLabel>Map</SectionLabel>
        <div style={{ position: 'relative', width: '100%', height: '380px', borderRadius: '10px', overflow: 'hidden', border: '1px solid ' + t.border, backgroundColor: t.surfaceAlt }}>
          <iframe
            title="Property location"
            src={embedSrc}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>

        {fullAddress && (
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
              <MapPin size={14} style={{ color: t.textSub, flexShrink: 0 }} />
              <div style={{ fontSize: '0.82rem', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullAddress}</div>
            </div>
            <a href={externalUrl} target="_blank" rel="noopener noreferrer" style={{
              fontSize: '0.75rem', fontWeight: '600', color: t.accent, textDecoration: 'none',
              padding: '6px 12px', borderRadius: '6px', border: '1px solid ' + t.accentBorder,
              backgroundColor: t.accentLight, whiteSpace: 'nowrap', flexShrink: 0
            }}>
              Open in Maps
            </a>
          </div>
        )}

        <SectionLabel mt="22px">Coordinates</SectionLabel>
        <InfoRow label="Latitude" value={lat.toFixed(6)} />
        <InfoRow label="Longitude" value={lng.toFixed(6)} />

        <div style={{ marginTop: '24px', padding: '14px 16px', backgroundColor: t.surfaceAlt, border: '1px solid ' + t.border, borderRadius: '8px' }}>
          <div style={{ fontSize: '0.72rem', color: t.textSub, lineHeight: '1.65' }}>
            <strong style={{ color: t.textMid, fontWeight: '600' }}>Data Notice</strong> — Map location is approximate and based on geocoded MLS or public-record data. Always confirm the exact property location with the listing agent prior to scheduling showings.
          </div>
        </div>
      </div>
    );
  };

  const tabContent = { overview: <OverviewTab />, details: <DetailsTab />, location: <LocationTab />, financials: <FinancialsTab /> };

  return createPortal(
    <>
      <style>{`
        .pdm-scroll::-webkit-scrollbar { width: 4px; }
        .pdm-scroll::-webkit-scrollbar-track { background: transparent; }
        .pdm-scroll::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.22); border-radius: 2px; }
        .pdm-arrow { transition: background-color 0.15s; }
        .pdm-arrow:hover { background-color: rgba(0,0,0,0.75) !important; }
        .pdm-thumb { transition: opacity 0.15s; cursor: pointer; }
        .pdm-thumb:hover { opacity: 1 !important; }
      `}</style>

      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: t.overlay, backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000001, padding: '20px' }}>
        <div onClick={e => e.stopPropagation()} style={{ backgroundColor: t.surface, borderRadius: '14px', width: '100%', maxWidth: '1080px', height: '86vh', display: 'flex', border: '1px solid ' + t.border, boxShadow: t.shadow, overflow: 'hidden' }}>

          {/* ── LEFT: photos ───────────────────────────────────────────────── */}
          <div style={{ width: '400px', flexShrink: 0, backgroundColor: t.surfaceDark, display: 'flex', flexDirection: 'column', borderRight: '1px solid ' + t.border }}>

            {/* Main photo */}
            <div style={{ position: 'relative', flex: 1, minHeight: 0, backgroundColor: t.surfaceDark, overflow: 'hidden', cursor: 'default' }}>
              {allImages.length > 0
                ? <img src={allImages[currentImageIndex]} alt="Property" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', objectPosition: 'center', transform: 'scale(1.08)', transformOrigin: 'center' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.textSub, fontSize: '0.875rem' }}>No photos</div>
              }
              {allImages.length > 1 && (
                <>
                  <button className="pdm-arrow" onClick={e => { e.stopPropagation(); setCurrentImageIndex(p => p === 0 ? allImages.length - 1 : p - 1); }} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.48)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronLeft size={14} />
                  </button>
                  <button className="pdm-arrow" onClick={e => { e.stopPropagation(); setCurrentImageIndex(p => p === allImages.length - 1 ? 0 : p + 1); }} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.48)', border: 'none', borderRadius: '50%', width: '30px', height: '30px', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={14} />
                  </button>
                  <div style={{ position: 'absolute', bottom: '10px', right: '12px', backgroundColor: lm ? 'rgba(255,255,255,0.82)' : 'rgba(0,0,0,0.55)', borderRadius: '20px', padding: '2px 9px', fontSize: '0.7rem', fontWeight: '600', color: lm ? '#111' : '#fff', border: lm ? '1px solid rgba(0,0,0,0.1)' : 'none' }}>
                    {currentImageIndex + 1}/{allImages.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div style={{ padding: '8px 10px', borderTop: '1px solid ' + t.border, backgroundColor: t.surfaceDark }}>
                <div className="pdm-scroll" style={{ display: 'flex', gap: '5px', overflowX: 'auto', paddingBottom: '2px' }}>
                  {allImages.map((img, i) => (
                    <div key={i} className="pdm-thumb" onClick={() => setCurrentImageIndex(i)} style={{ flexShrink: 0, width: '52px', height: '38px', borderRadius: '4px', overflow: 'hidden', border: '2px solid ' + (currentImageIndex === i ? t.gold : 'transparent'), opacity: currentImageIndex === i ? 1 : 0.5 }}>
                      <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Agent / MLS footer */}
            <div style={{ padding: '14px 18px', borderTop: '1px solid ' + t.border }}>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: t.text, marginBottom: '1px' }}>{property.listedBy?.agentName || property.mlsData?.mlsAgent?.fullName || 'Agent N/A'}</div>
                <div style={{ fontSize: '0.78rem', color: t.textSub }}>{property.listedBy?.agency || property.mlsData?.mlsOffice?.name || ''}</div>
              </div>
              {(() => {
                const mlsNames = { COMLMLS: 'REcolorado', COPIKES: 'Pikes Peak MLS', COIRES: 'IRES', TXDALLAS: 'NTREIS' };
                const boardName = mlsNames[property.mlsData?.mlsBoardCode];
                if (!boardName) return null;
                return (
                  <div style={{ fontSize: '0.72rem', color: t.textSub }}>
                    <span>{boardName}</span>
                    {property.mlsData.mlsNumber && <span style={{ margin: '0 4px' }}>·</span>}
                    {property.mlsData.mlsNumber && <span>MLS# {property.mlsData.mlsNumber}</span>}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── RIGHT: tabs ────────────────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Tab bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 22px', borderBottom: '1px solid ' + t.border, flexShrink: 0, marginTop: '8px' }}>
              <div style={{ display: 'flex' }}>
                {tabs.map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '16px 16px 14px',
                    border: 'none', borderBottom: '2px solid ' + (activeTab === tab.id ? t.accent : 'transparent'),
                    cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: activeTab === tab.id ? '600' : '400',
                    color: activeTab === tab.id ? t.text : t.textSub,
                    backgroundColor: 'transparent',
                    whiteSpace: 'nowrap', transition: 'all 0.15s',
                    marginBottom: '-1px'
                  }}>
                    {tab.icon} {tab.label}
                  </button>
                ))}
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: t.textSub, cursor: 'pointer', padding: '6px', display: 'flex', borderRadius: '6px', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = t.text}
                onMouseLeave={e => e.currentTarget.style.color = t.textSub}>
                <X size={17} />
              </button>
            </div>

            {/* Content */}
            <div className="pdm-scroll" style={{ flex: 1, overflowY: 'auto', padding: '22px 24px 28px', minHeight: 0 }}>
              {tabContent[activeTab]}
            </div>
          </div>
        </div>
      </div>

    </>,
    document.body
  );
};

export default PropertyDetailsModal;